import * as React from 'react';
import { GripVertical, Loader2, Save, Sparkles, Wand2 } from 'lucide-react';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input, Textarea } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Switch } from '../../components/ui/switch';
import { Skeleton } from '../../components/ui/skeleton';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../components/ui/select';
import { useToast } from '../../components/ui/toast';
import { HeaderActions } from '../../components/HeaderActions';
import {
  useOnboardingConfig,
  useUpsertOnboardingConfig,
} from '../../hooks/useOnboarding';
import type { OnboardingConfigUpsertInput } from '@cp3/shared';

const GEMINI_MODELS = [
  { id: 'gemini-2.5-flash', label: 'Gemini 2.5 Flash (schnell, preiswert)' },
  { id: 'gemini-2.5-pro', label: 'Gemini 2.5 Pro (höchste Qualität)' },
  { id: 'gemini-1.5-flash', label: 'Gemini 1.5 Flash (Legacy)' },
  { id: 'gemini-1.5-pro', label: 'Gemini 1.5 Pro (Legacy)' },
];

const DEFAULT_STEP_KEYS = [
  'company',
  'contact',
  'hours',
  'closures',
  'offerings',
  'pricing',
  'booking',
  'faq',
  'emergency',
  'documents',
  'personality',
  'summary',
] as const;

const DEFAULT_LABELS: Record<string, string> = {
  company: 'Unternehmen',
  contact: 'Kontakt & Adresse',
  hours: 'Öffnungszeiten',
  closures: 'Sonderzeiten & Feiertage',
  offerings: 'Angebot',
  pricing: 'Preise',
  booking: 'Buchungsprozess',
  faq: 'Häufige Fragen',
  emergency: 'Notfälle',
  documents: 'Wissensdokumente',
  personality: 'Persönlichkeit & Stil',
  summary: 'Zusammenfassung',
};

export function OnboardingConfigPage() {
  const { data, isLoading } = useOnboardingConfig();
  const upsert = useUpsertOnboardingConfig();
  const { toast } = useToast();

  const [model, setModel] = React.useState('gemini-2.5-flash');
  const [steps, setSteps] = React.useState<OnboardingConfigUpsertInput['steps']>([]);

  React.useEffect(() => {
    if (!data) return;
    setModel(data.config.geminiModel ?? 'gemini-2.5-flash');
    const serverSteps = data.config.steps ?? [];
    if (serverSteps.length === 0) {
      // Seed from defaults if the config doc is empty.
      setSteps(
        DEFAULT_STEP_KEYS.map((key) => ({
          key,
          label: DEFAULT_LABELS[key] ?? key,
          enabled: true,
        })),
      );
    } else {
      setSteps(serverSteps);
    }
  }, [data]);

  function patchStep(idx: number, patch: Partial<OnboardingConfigUpsertInput['steps'][number]>) {
    setSteps((prev) => prev.map((s, i) => (i === idx ? { ...s, ...patch } : s)));
  }

  function move(idx: number, direction: -1 | 1) {
    setSteps((prev) => {
      const next = [...prev];
      const target = idx + direction;
      if (target < 0 || target >= next.length) return prev;
      const tmp = next[idx]!;
      next[idx] = next[target]!;
      next[target] = tmp;
      return next;
    });
  }

  async function onSave() {
    try {
      await upsert.mutateAsync({ geminiModel: model, steps });
      toast({ title: 'Onboarding-Konfig aktualisiert' });
    } catch (err) {
      toast({
        title: 'Fehler',
        description: (err as Error).message,
        variant: 'destructive',
      });
    }
  }

  if (isLoading) {
    return (
      <div className="space-y-3">
        <Skeleton className="h-10 w-1/3" />
        <Skeleton className="h-32 w-full" />
        <Skeleton className="h-64 w-full" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <HeaderActions>
        <Button onClick={() => void onSave()} disabled={upsert.isPending} size="sm">
          {upsert.isPending ? (
            <Loader2 className="mr-2 h-4 w-4 animate-spin" />
          ) : (
            <Save className="mr-2 h-4 w-4" />
          )}
          Speichern
        </Button>
      </HeaderActions>

      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Wand2 className="h-5 w-5 text-primary" />
            Komprimierungs-Modell
          </CardTitle>
          <CardDescription>
            Welches Gemini-Modell die Onboarding-Antworten der User in Wissensbasis +
            Konversations-Stil komprimiert. Flash ist 5–10× schneller und ~20× günstiger
            als Pro — für die meisten Komprimierungen reicht es. Für sehr komplexe
            Branchen oder lange Dokumente kannst du auf Pro hochgehen.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-3">
          <div className="space-y-1.5">
            <Label>Modell</Label>
            <Select value={model} onValueChange={setModel}>
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {GEMINI_MODELS.map((m) => (
                  <SelectItem key={m.id} value={m.id}>
                    {m.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <p className="text-[11px] text-muted-foreground">
            Voraussetzung: <code>PLATFORM_GOOGLE_AI_API_KEY</code> ist im Secret Manager
            gesetzt (Entwicklereinstellungen → Platform-Keys).
          </p>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Sparkles className="h-5 w-5 text-primary" />
            Wizard-Schritte
          </CardTitle>
          <CardDescription>
            Welche Schritte erscheinen im User-Wizard und in welcher Reihenfolge.
            Deaktivierte Schritte verschwinden komplett — die zugehörigen Felder
            bleiben aber im Datenmodell, damit alte Eingaben nicht verloren gehen,
            falls du den Schritt später wieder einschaltest.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-2">
          {steps.map((s, idx) => (
            <div
              key={s.key}
              className="flex items-start gap-3 rounded-md border border-border bg-muted/10 p-3"
            >
              <div className="flex flex-col items-center pt-1.5">
                <button
                  type="button"
                  onClick={() => move(idx, -1)}
                  disabled={idx === 0}
                  className="p-0.5 text-muted-foreground hover:text-foreground disabled:opacity-30"
                  aria-label="Höher sortieren"
                >
                  ▲
                </button>
                <GripVertical className="size-3 text-muted-foreground" />
                <button
                  type="button"
                  onClick={() => move(idx, 1)}
                  disabled={idx === steps.length - 1}
                  className="p-0.5 text-muted-foreground hover:text-foreground disabled:opacity-30"
                  aria-label="Tiefer sortieren"
                >
                  ▼
                </button>
              </div>
              <div className="flex-1 space-y-2">
                <div className="grid gap-2 sm:grid-cols-[1fr_2fr]">
                  <Input
                    value={s.label}
                    onChange={(e) => patchStep(idx, { label: e.target.value })}
                    placeholder="Sichtbarer Titel"
                  />
                  <Input
                    value={s.key}
                    disabled
                    className="font-mono text-xs"
                  />
                </div>
                <Textarea
                  rows={2}
                  placeholder="Hilfetext (optional, erscheint über dem Schritt)"
                  value={s.helperText ?? ''}
                  onChange={(e) => patchStep(idx, { helperText: e.target.value })}
                />
              </div>
              <div className="flex flex-col items-center gap-1 pt-1">
                <Switch
                  checked={s.enabled}
                  onCheckedChange={(c) => patchStep(idx, { enabled: c })}
                />
                <span className="text-[10px] text-muted-foreground">
                  {s.enabled ? 'aktiv' : 'aus'}
                </span>
              </div>
            </div>
          ))}
        </CardContent>
      </Card>
    </div>
  );
}
